'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Modal, ModalFooter } from '../modal';
import { AdaptiveFormKeyboardHints } from '../form/adaptive-form';
import { FormDirtyContext } from '../form/form-dirty-context';
import { useDataTableDefaults } from '../data/data-table';
import { useDiscardGuard } from './use-discard-guard';
import { cn } from '../../utils/cn';
// Modern small modals: white footer, 36px buttons (the pages' raw <button>s keep their own colours)
const MODERN_FOOTER = '!bg-white dark:!bg-gray-800 !py-3 !border-gray-100 dark:!border-gray-700 [&_button]:!h-9 [&_button]:!py-0 [&_button]:!px-4 [&_button]:!text-sm [&_button]:!rounded-lg';
/**
 * EnhancedFormModal - A reusable modal component with consistent design
 *
 * It used to be copied into the parking, payroll and purchase packages; those
 * files now just re-export this one.
 *
 * Features:
 * - Enhanced header with icon and color
 * - Optional keyboard hints section
 * - Customizable content area
 * - Customizable footer
 * - Consistent styling across all forms
 *
 * @example
 * <EnhancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Create New Country"
 *   description="Configure country details"
 *   icon={<Globe className="w-8 h-8" />}
 *   showKeyboardHints
 * >
 *   <AdaptiveForm>
 *     <AdaptiveField ... />
 *   </AdaptiveForm>
 * </EnhancedFormModal>
 */
export function EnhancedFormModal({ isOpen, onClose, title, description, icon, size = 'md', children, footer, showKeyboardHints = true, contentPadding = false, headerColor = 'primary', className, confirmDiscard = true, }) {
    // Modern appearance (follows the module's table switch): every modal gets the light tinted header.
    // Classic modules keep their header colour unless the page asks for headerColor="tinted".
    const isModern = useDataTableDefaults().appearance === 'modern';
    const isTintedLook = isModern || headerColor === 'tinted';
    // ── Unsaved changes ── "Discard changes?" on ✕ / Esc / overlay / Cancel (shared hook)
    const guard = useDiscardGuard({ isOpen, onClose, enabled: confirmDiscard });
    // The footer must stay a direct ModalFooter child: Modal pins it below the scrolling content
    const isModalFooter = React.isValidElement(footer) && footer.type === ModalFooter;
    const modalFooter = isModalFooter
        ? React.cloneElement(footer, {
            className: isTintedLook ? cn(footer.props.className, MODERN_FOOTER) : footer.props.className,
            onClickCapture: guard.interceptCancel,
        })
        : footer;
    return (_jsxs(FormDirtyContext.Provider, { value: guard.dirtyContextValue, children: [_jsxs(Modal, { isOpen: isOpen, onClose: guard.requestClose, title: title, description: description, size: size, headerStyle: "enhanced", icon: icon, headerColor: isTintedLook ? 'tinted' : headerColor, contentPadding: false, className: className, children: [showKeyboardHints && !isTintedLook && (_jsx("div", { className: "px-6 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700", children: _jsx(AdaptiveFormKeyboardHints, {}) })), _jsx("div", { className: contentPadding ? 'px-6 py-4' : '', onChangeCapture: guard.trackChange, children: children }), modalFooter] }), guard.dialog] }));
}
